import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";

import UserController from "@/api/controller/userController";
import { Form, FormTextField } from "@/base/components/Form";
import { Button, H5, P } from "@/base/components/Global";
import { Col, Row } from "@/base/components/Layout";
import { useMediaQuery } from "@/base/components/MediaQueryProvider";
import SimpleModalWrapper from "@/base/components/Modal/SimpleModalWrapper";
import { BACK_CIRCLE_ICON } from "@/constants/blobIcons";
import { NavigationPath } from "@/constants/navigation";
import { createUseStyles } from "@/theme";

const useStyles = createUseStyles(({ spacing, sizes }) => ({
  btnContainer: {
    alignSelf: "flex-start",
  },
  emailContainer: {
    [sizes.md]: {
      marginTop: spacing.x4l,
    },
  },
}));

const FormSchema = z.object({
  email: z.string().email(),
});

type FormType = z.infer<typeof FormSchema>;

const RequestResetPasswordPage = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [currentModal, setCurrentModal] = useState<"success" | "error">();
  const [{ isCurrentSizeMd }] = useMediaQuery();

  const form = useForm<FormType>({
    mode: "onChange",
    resolver: zodResolver(FormSchema),
    defaultValues: {
      email: "",
    },
  });

  const {
    formState: { isValid, isSubmitting, isDirty },
  } = form;

  const onModalClose = async () => {
    setCurrentModal(undefined);
    if (currentModal === "success") {
      await navigate(NavigationPath.Home);
    }
  };

  const onSubmit = async ({ email }: FormType) => {
    const { isResponseOk } = await UserController.requestPasswordReset({
      body: { email },
    });

    setCurrentModal(isResponseOk ? "success" : "error");
  };

  return (
    <>
      <Form {...form} onSubmitValid={onSubmit}>
        <Row>
          <Col fullWidth noMargin alignCenter>
            <Col directionColumn fullWidth noMargin colMd={9}>
              <Col noMarginBottom>
                <H5 prefixIcon={BACK_CIRCLE_ICON} gray1>
                  {t("reset_password_page_title")}
                </H5>
              </Col>
              <Col noMarginTop>
                <P noMarginTop small gray3>
                  {t("reset_password_page_subtitle")}
                </P>
              </Col>
              <Col fullWidth>
                <P>{t("reset_password_page_content")}</P>
              </Col>
              <Col
                noMargin
                fullWidth
                directionColumn={!isCurrentSizeMd}
                className={classes.emailContainer}
              >
                <Col noMargin directionColumn col={12} colMd={6} fullWidth>
                  <Col fullWidth>
                    <FormTextField
                      id="email-input-bar"
                      name="email"
                      type="email"
                      autoComplete="email"
                      label={t("field_enter_email_label")}
                      placeholder={t("field_email_placeholder")}
                    />
                  </Col>
                  <Col fullWidth>
                    <P gray3 xSmall>
                      {t("reset_password_page_email_description")}
                    </P>
                  </Col>
                </Col>

                <Col
                  fullWidth
                  colMd={6}
                  col={12}
                  className={classes.btnContainer}
                >
                  <Button
                    type="submit"
                    isDisabled={!isValid || !isDirty}
                    isLoading={isSubmitting}
                  >
                    {t("reset_password_page_recover_btn")}
                  </Button>
                </Col>
              </Col>
            </Col>
          </Col>
        </Row>
      </Form>
      <SimpleModalWrapper
        isOpen={currentModal === "success"}
        title={t("reset_password_page_success_modal_title")}
        onClose={onModalClose}
        content={t("reset_password_page_success_modal_content")}
      />
      <SimpleModalWrapper
        isOpen={currentModal === "error"}
        title={t("reset_password_page_error_modal_title")}
        onClose={onModalClose}
        content={t("reset_password_page_error_modal_content")}
      />
    </>
  );
};

export default RequestResetPasswordPage;
